<template>
  <template v-if="!mdAndUp">
    <v-expansion-panels
      v-model="mobileExpandedPanel"
      class="custom-panels"
      elevation="0"
    >
      <v-expansion-panel class="filter-section" elevation="0">
        <v-expansion-panel-title class="pa-0">
          <div class="d-flex justify-space-between align-center py-1 w-100">
            <h5 class="text-secondary font-weight-bold">More Filters</h5>
          </div>
        </v-expansion-panel-title>
        <v-divider v-if="mobileExpandedPanel === 0" class="mt-3"></v-divider>
        <v-expansion-panel-text class="filter-content">
          <v-expansion-panels
            v-model="expandedPanels"
            multiple
            class="custom-panels"
            elevation="0"
          >
            <template v-if="hasFilters">
              <template
                v-for="(filter, index) in reportsStore.filters"
                :key="filter.id"
              >
                <v-expansion-panel class="filter-section" elevation="0">
                  <v-expansion-panel-title class="pa-0">
                    <div
                      class="d-flex justify-space-between align-center py-4 w-100"
                    >
                      <div class="section-title">{{ filter.name }}</div>
                    </div>
                  </v-expansion-panel-title>
                  <v-expansion-panel-text class="filter-content">
                    <v-checkbox
                      v-for="option in filter.options"
                      :key="option.value"
                      :model-value="isOptionSelected(filter, option)"
                      :label="option.label"
                      density="compact"
                      color="primary"
                      hide-details
                      class="bordered-checkbox"
                      @update:model-value="
                        (val: boolean | null) => {
                          if (filter.id === 'subject') {
                            handleSubjectChange(option.value, val ?? false);
                          } else {
                            handleFilterChange(
                              filter.id,
                              option.value,
                              val ?? false,
                            );
                          }
                        }
                      "
                    ></v-checkbox>
                    <a
                      v-if="filter.id === 'format'"
                      class="reset-link text-primary pt-2"
                      @click.stop="glossaryStore.openGlossary('ela')"
                    >
                      What is ELA?
                    </a>
                  </v-expansion-panel-text>
                </v-expansion-panel>
                <v-divider
                  v-if="index < reportsStore.filters.length - 1"
                ></v-divider>
              </template>
            </template>
            <template v-else>
              <div
                v-if="!isLoading && !reportsStore.error && !hasFilters"
                class="text-body-2 pa-4"
              >
                No filters available
              </div>
              <div v-else-if="reportsStore.error" class="text-error pa-4">
                {{ reportsStore.error }}
              </div>
              <div v-else class="pa-4 text-grey">Loading filters...</div>
            </template>
          </v-expansion-panels>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>
  </template>
  <v-expansion-panels
    v-else
    class="custom-panels"
    elevation="0"
    multiple
    v-model="expandedPanels"
  >
    <template v-if="hasFilters">
      <template
        v-for="(filter, index) in reportsStore.filters"
        :key="filter.id"
      >
        <v-expansion-panel class="filter-section" elevation="0">
          <v-expansion-panel-title class="pa-0">
            <div class="d-flex justify-space-between align-center py-4 w-100">
              <div class="section-title">{{ filter.name }}</div>
            </div>
          </v-expansion-panel-title>
          <v-expansion-panel-text class="filter-content">
            <v-checkbox
              v-for="option in filter.options"
              :key="option.value"
              :model-value="isOptionSelected(filter, option)"
              :label="option.label"
              density="compact"
              color="primary"
              hide-details
              class="bordered-checkbox"
              @update:model-value="
                (val: boolean | null) => {
                  if (filter.id === 'subject') {
                    handleSubjectChange(option.value, val ?? false);
                  } else {
                    handleFilterChange(filter.id, option.value, val ?? false);
                  }
                }
              "
            ></v-checkbox>
            <a
              v-if="filter.id === 'format'"
              class="reset-link text-primary pt-2"
              @click.stop="glossaryStore.openGlossary('ela')"
            >
              What is ELA?
            </a>
          </v-expansion-panel-text>
        </v-expansion-panel>
        <v-divider v-if="index < reportsStore.filters.length - 1"></v-divider>
      </template>
    </template>
    <template v-else>
      <div
        v-if="!isLoading && !reportsStore.error && !hasFilters"
        class="text-body-2 pa-4"
      >
        No filters available
      </div>
      <div v-else-if="reportsStore.error" class="text-error pa-4">
        {{ reportsStore.error }}
      </div>
      <div v-else class="pa-4 text-grey">Loading filters...</div>
    </template>
  </v-expansion-panels>
</template>

<script setup lang="ts">
  import { ref, watch, computed, onMounted, nextTick } from 'vue';
  import { useGlossaryStore, useReportsStore } from '../../store';
  import { useDisplay } from 'vuetify';
  import { useRouter, useRoute } from 'vue-router';
  import type {
    FilterGroup,
    FilterOption,
  } from '../../store/modules/reportsStore';

  // Custom debounce function implementation to replace @vueuse/core dependency
  const useDebounceFn = (fn: (...args: any[]) => void, delay: number) => {
    let timeout: number | null = null;
    return (...args: any[]) => {
      if (timeout !== null) {
        clearTimeout(timeout);
      }
      timeout = window.setTimeout(() => {
        fn(...args);
        timeout = null;
      }, delay);
    };
  };

  const display = useDisplay();
  const mdAndUp = computed(() => display.mdAndUp.value);
  const mobileExpandedPanel = ref<number | null>(null);
  const expandedPanels = ref<number[]>([]);
  const router = useRouter();
  const route = useRoute();

  const glossaryStore = useGlossaryStore();
  const reportsStore = useReportsStore();
  const isUpdatingFilter = ref(false);

  // Function to find the index of a filter by its ID
  const findFilterIndex = (filterId: string) => {
    return reportsStore.filters.findIndex((filter) => filter.id === filterId);
  };

  // Function to expand the panel for the selected subject
  const expandSubjectPanel = () => {
    const subjectIndex = findFilterIndex('subject');
    if (subjectIndex !== -1 && !expandedPanels.value.includes(subjectIndex)) {
      expandedPanels.value = [...expandedPanels.value, subjectIndex];
    }
  };

  // Watch for route changes to expand the subject panel
  watch(
    () => route.params.subject,
    (newSubject) => {
      if (newSubject) {
        nextTick(() => {
          expandSubjectPanel();
        });
      }
    },
    { immediate: true },
  );

  // Function to update URL with current filter state
  const updateRoute = useDebounceFn(() => {
    if (isUpdatingFilter.value) return;

    const query: Record<string, string[]> = {};
    const subject = reportsStore.selectedSubjects[0];

    if (reportsStore.selectedFormats.length > 0) {
      query.formats = reportsStore.selectedFormats;
    }
    if (reportsStore.selectedGrades.length > 0) {
      query.grades = reportsStore.selectedGrades;
    }

    // If there's a subject selected, use it in the path
    if (subject) {
      router.replace({
        path: `/reports/${subject.toLowerCase()}`,
        query,
      });
    } else {
      // If no subject selected, use the base path
      router.replace({
        path: '/reports',
        query,
      });
    }
  }, 300);

  // Watch for filter changes and update URL
  watch(
    [
      () => [...reportsStore.selectedSubjects],
      () => [...reportsStore.selectedFormats],
      () => [...reportsStore.selectedGrades],
    ],
    () => {
      if (!isUpdatingFilter.value) {
        updateRoute();
      }
    },
    { deep: true },
  );

  // Using cached values
  const optionSelectedCache = new Map();

  // Define an optimized version that caches results
  function isOptionSelected(
    filter: FilterGroup,
    option: FilterOption,
  ): boolean {
    const cacheKey = `${filter.id}-${option.value}`;

    // Check the cache first for better performance
    if (optionSelectedCache.has(cacheKey)) {
      return optionSelectedCache.get(cacheKey);
    }

    let result = false;
    // Ensure consistent case comparison for subject
    const subjectValue = option.value.toUpperCase();

    switch (filter.id) {
      case 'subject':
        // Check if the uppercase option value exists in the selectedSubjects array
        result = reportsStore.selectedSubjects.some(
          (subj) => subj.toUpperCase() === subjectValue,
        );
        break;
      case 'format':
        result = reportsStore.selectedFormats.includes(option.value);
        break;
      case 'grade':
        result = reportsStore.selectedGrades.includes(option.value);
        break;
      case 'standard':
        result = reportsStore.selectedStandards.includes(option.value);
        break;
      default:
        result = false; // Default to false for unhandled types
    }

    // Store result in cache
    optionSelectedCache.set(cacheKey, result);
    return result;
  }

  // Clear the cache when selections change
  watch(
    [
      () => [...reportsStore.selectedSubjects],
      () => [...reportsStore.selectedFormats],
      () => [...reportsStore.selectedGrades],
      () => [...reportsStore.selectedStandards],
    ],
    () => {
      // Clear the cache
      optionSelectedCache.clear();
    },
  );

  const debouncedFilterChange = useDebounceFn(
    (filterId: string, value: string, selected: boolean) => {
      if (isUpdatingFilter.value) return;

      isUpdatingFilter.value = true;
      // Use requestAnimationFrame to ensure UI remains responsive
      requestAnimationFrame(() => {
        try {
          switch (filterId) {
            case 'format':
              reportsStore.setFormat(value, selected);
              break;
            case 'grade':
              reportsStore.setGrade(value, selected);
              break;
            case 'standard':
              reportsStore.setStandard(value, selected);
              break;
            default:
              // Default to format
              reportsStore.setFormat(value, selected);
          }
        } finally {
          // Always reset the flag
          isUpdatingFilter.value = false;
        }
      });
    },
    100,
  );

  // Updated handler for subject changes to enforce single selection
  function handleSubjectChange(value: string, selected: boolean) {
    isUpdatingFilter.value = true;
    try {
      // If checking a box, pass the value to the store action
      // which will handle setting it as the only selected subject.
      if (selected) {
        reportsStore.setSubject(value, true);
      }
      // If unchecking a box, prevent it if it's the only one selected.
      // The store action will handle the logic to prevent removal.
      else if (
        reportsStore.selectedSubjects.length === 1 &&
        reportsStore.selectedSubjects[0].toUpperCase() === value.toUpperCase()
      ) {
        // Re-check the box visually by forcing the model value update to fail effectively
        // This requires ensuring the :model-value binding correctly reflects the store state.
        // No action needed here as the store won't change.
      } else {
        // This case should technically not be reached if logic is correct,
        // but handles unchecking if multiple were somehow selected.
        reportsStore.setSubject(value, false);
      }
    } finally {
      isUpdatingFilter.value = false;
    }
  }

  function handleFilterChange(
    filterId: string,
    value: string,
    selected: boolean,
  ) {
    isUpdatingFilter.value = true;
    try {
      debouncedFilterChange(filterId, value, selected);
    } finally {
      isUpdatingFilter.value = false;
    }
  }

  // Add computed properties for safe state access
  const hasFilters = computed(() => {
    const filters = reportsStore.filters;
    return Array.isArray(filters) && filters.length > 0;
  });

  const isLoading = computed(() => reportsStore.loading);
</script>

<style lang="scss" scoped>
  .filter-section {
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);
    contain: content; /* Optimize rendering performance */
  }

  .section-title {
    font-size: 14px;
    font-weight: 400;
  }

  .filter-content {
    padding-bottom: 16px;
    contain: content; /* Optimize rendering performance */

    :deep(.v-checkbox) {
      .v-label {
        font-size: 13px;
      }

      /* Improve touch target size for mobile */
      @media (max-width: 768px) {
        .v-selection-control {
          min-height: 40px;
          touch-action: manipulation;
        }
      }
    }
  }

  .custom-panels {
    background: transparent !important;
    box-shadow: none !important;
    contain: layout style; /* Optimize rendering performance */

    :deep(.v-expansion-panel) {
      background: transparent !important;
      box-shadow: none !important;
      border: none !important;
      contain: content;

      .v-expansion-panel-title__icon {
        color: rgb(var(--v-theme-primary)) !important;
      }
    }

    :deep(.v-expansion-panel-title) {
      min-height: unset !important;
      border: none !important;
      touch-action: manipulation; /* Improve touch responsiveness */
    }

    :deep(.v-expansion-panel-title__overlay) {
      display: none;
    }

    :deep(.v-expansion-panel-title--active) {
      min-height: unset !important;
      border: none !important;
    }

    :deep(.v-expansion-panel-text__wrapper) {
      padding: 0;
    }

    :deep(.v-expansion-panel::after) {
      border: none !important;
    }
  }

  .reset-link {
    cursor: pointer;
    text-decoration: underline;
    margin-top: 0;
    display: inline-block;
    touch-action: manipulation; /* Improve touch responsiveness */
    padding: 8px 0; /* Increase touch target size */
  }
</style>
